/**
 * Leaderboard client.
 *
 * The endpoint is read from a <meta> tag so the deployed page can be pointed
 * at an API without rebuilding the bundle — and so the game works perfectly
 * with no endpoint at all, falling back to local scores.
 *
 * Submissions carry the full replay, not just a number. The server re-runs the
 * simulation and keeps the score only if it can reproduce it.
 */

import { encodeTurns, type Turn } from '../core/replay'
import { RULES_VERSION } from '../core/rules'
import type { ModeId, RunSummary } from '../core/types'
import { store } from '../storage'

export interface Entry {
  rank: number
  name: string
  score: number
  motes: number
  bites: number
  longest: number
  coil: number
  at: number
  you?: boolean
}

export interface Board {
  entries: Entry[]
  source: 'online' | 'local'
  yourRank?: number
  note?: string
}

export interface Submission {
  run: RunSummary
  turns: readonly Turn[]
  name: string
}

function endpoint(): string | null {
  const meta = document.querySelector('meta[name="ouroboros-api"]')
  const url = meta?.getAttribute('content')?.trim()
  if (!url || url.startsWith('{{')) return null
  return url.replace(/\/$/, '')
}

export const hasOnlineBoard = (): boolean => endpoint() !== null

const TIMEOUT = 8000

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const base = endpoint()
  if (!base) throw new Error('offline')
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT)
  try {
    const res = await fetch(`${base}${path}`, {
      ...init,
      signal: ctrl.signal,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    })
    if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => '')}`.trim())
    return (await res.json()) as T
  } finally {
    clearTimeout(timer)
  }
}

/** Local scores shaped like a board, so the UI has one code path. */
function localBoard(mode: ModeId, note?: string): Board {
  const entries = store.localTop(mode, 25).map((s, i) => ({
    rank: i + 1,
    name: s.name || 'you',
    score: s.score,
    motes: s.motes,
    bites: s.bites,
    longest: s.longest,
    coil: s.coil,
    at: s.at,
    you: true,
  }))
  return { entries, source: 'local', note }
}

export async function fetchBoard(mode: ModeId, scope: 'all' | 'today' = 'all'): Promise<Board> {
  if (!hasOnlineBoard()) return localBoard(mode)
  try {
    const data = await call<{ entries: Entry[]; yourRank?: number }>(
      `/scores?mode=${encodeURIComponent(mode)}&scope=${scope}&v=${RULES_VERSION}`,
    )
    return { entries: data.entries ?? [], source: 'online', yourRank: data.yourRank }
  } catch (err) {
    return localBoard(mode, `Couldn't reach the leaderboard (${(err as Error).message}). Showing your local runs.`)
  }
}

export interface SubmitResult {
  ok: boolean
  rank?: number
  total?: number
  message?: string
  queued?: boolean
}

export async function submitRun({ run, turns, name }: Submission): Promise<SubmitResult> {
  const payload = {
    v: RULES_VERSION,
    mode: run.mode,
    seed: run.seed,
    turns: encodeTurns(turns),
    name: name.slice(0, 16),
    claim: {
      score: run.score,
      ticks: run.ticks,
      motes: run.motes,
      embers: run.embers,
      bites: run.bites,
      longest: run.longest,
      coil: run.coil,
    },
  }

  if (!hasOnlineBoard()) return { ok: false, message: 'Leaderboard not configured — saved locally.' }

  try {
    const data = await call<{ rank: number; total: number; message?: string }>('/scores', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return { ok: true, rank: data.rank, total: data.total, message: data.message }
  } catch (err) {
    // Network blips shouldn't cost someone their best run — hold it and try
    // again next time the page loads.
    store.queuePending(payload)
    return { ok: false, queued: true, message: `Saved offline, will retry: ${(err as Error).message}` }
  }
}

/** Fire-and-forget retry of anything stranded by an earlier failure. */
export async function flushPending(): Promise<void> {
  if (!hasOnlineBoard()) return
  const queued = store.pending
  if (!queued.length) return
  for (const payload of queued) {
    try {
      await call('/scores', { method: 'POST', body: JSON.stringify(payload) })
    } catch {
      return // still down; keep the queue for next time
    }
  }
  store.clearPending()
}
